import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { SUBMIT_ROUTE } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useApolloClient } from '@apollo/client';
import { SUBMIT_RECONCILE } from '../graphql/queries';
import {
    Body1,
    Body2,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import { MODALS } from '../constants';
import { PlanningContext } from '../context';
import { useAfterHours } from '../hooks';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { checkRoutesCeilings } from '../util/resourceCeilings';
import { routeName } from '@/utilities/routeNames';
import CeilingWarnings from './CeilingWarnings';

const RouteSubmitInternalModal = () => {
    const {
        modalOpen,
        setModalOpen,
        actionableRoute,
        setActionableRoute,
        setError,
        setNotification,
        state: { routesToSubmitInternal, lockAllRoutesClicked },
        callbacks: { refetch, setRoutesToSubmitInternal, setLockAllRoutesClicked },
    } = useContext(PlanningContext);
    const client = useApolloClient();
    const [confirmationType, setConfirmationType] = useState('shipperPreference');
    const [loading, setLoading] = useState(false);
    const { circles, preferences_bypass_optimize } = useClientUser();

    const routes = useMemo(() => {
        if (routesToSubmitInternal?.length > 0) {
            return routesToSubmitInternal;
        }
        return actionableRoute ? [actionableRoute] : [];
    }, [routesToSubmitInternal, actionableRoute]);

    const afterHours = useAfterHours(routes[0]);

    // Covers the bulk path too: lockAllRoutes ends by opening this same modal.
    const { warned } = useMemo(() => checkRoutesCeilings(routes), [routes]);

    const hasAutoconfirmAllStops = useMemo(() => {
        const autoConfirm = circles?.['autoconfirm-all-stops'] === true;
        if (autoConfirm) {
            setConfirmationType('autoConfirm');
        }
        return autoConfirm;
    }, [circles]);

    const hasLockAllRoutes = useMemo(() => {
        return circles?.['lock-all-routes'] === true;
    }, [circles]);

    const handleModalClose = () => {
        setLoading(false);
        setModalOpen(null);
        setActionableRoute(null);
        setRoutesToSubmitInternal([]);
        setConfirmationType('shipperPreference');
        setLockAllRoutesClicked(false);
    };

    const missingPhoneNumbers = useMemo(() => {
        let ordersWithoutPhone = [];
        routes.forEach((r) => {
            r?.orders.forEach(({ type, order }) => {
                if (type !== 'PICKUP' && !order.dropoff_phone) {
                    ordersWithoutPhone.push(order.order_number);
                }
            });
        });
        if (ordersWithoutPhone.length > 0) {
            setConfirmationType('none');
        }
        return ordersWithoutPhone;
    }, [routes]);

    const handleSubmitInternal = async () => {
        const needsOptimize = preferences_bypass_optimize ? [] : routes.filter((route) => route?.need_to_optimize);
        if (needsOptimize.length > 0) {
            return setNotification({
                severity: 'warning',
                message: `Route${needsOptimize.length > 1 ? 's' : ''} ${needsOptimize
                    .map((r) => routeName(r) || r.route_number)
                    .join(', ')} may have been changed recently, please Map or Optimize route and try again`,
            });
        }

        try {
            setLoading(true);

            const payloads = routes.map((route) => {
                return {
                    routeId: route.route_id,
                    sendingTo: route.sending_to || 'internal',
                    sendSMS: confirmationType === 'sms',
                    useRobocall: confirmationType === 'robocall',
                    useShipperPreference: confirmationType === 'shipperPreference',
                    autoConfirm: confirmationType === 'autoConfirm',
                    lockAllRoutes: lockAllRoutesClicked && hasLockAllRoutes,
                };
            });

            const responses = await Promise.all(payloads.map((payload) => post(SUBMIT_ROUTE, payload)));

            if (responses.every((response) => response.status === 200)) {
                setNotification({
                    severity: 'success',
                    message: `Successfully submitted Route${routes.length > 1 ? 's' : ''} ${routes
                        .map((r) => routeName(r) || r.route_number)
                        .join(', ')} !`,
                });
                client
                    .query({
                        query: SUBMIT_RECONCILE,
                        variables: {
                            route_ids: routes.map((r) => r.route_id),
                            order_ids: routes.flatMap((r) => (r.orders || []).map((m) => m.order_id)),
                        },
                        fetchPolicy: 'network-only',
                    })
                    .catch(() => refetch());
                handleModalClose();
            } else {
                const error = responses.find((r) => r?.data?.errorMessage);
                captureException(new Error(error));
                setNotification({
                    severity: 'error',
                    message: error || 'Error submitting route, please verify all stops have timeframes and try again',
                });
                setLoading(false);
            }
        } catch (error) {
            setError(error, 'Error submitting route, please try again');
            setLoading(false);
        }
    };

    if (!routes?.length) return null;

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_INTERNAL} onClose={handleModalClose}>
            <ModalTitle>{`Confirm Submission`}</ModalTitle>
            <ModalContent>
                <CeilingWarnings warned={warned} showRouteNames={routes.length > 1} />
                {lockAllRoutesClicked && hasLockAllRoutes && !hasAutoconfirmAllStops && (
                    <GridItemRow>
                        <Alert severity="info">
                            Lock all routes is selected. Routes will be locked and sent to drivers regardless of
                            customer confirmation status.
                        </Alert>
                    </GridItemRow>
                )}
                {afterHours && confirmationType === 'sms' && (
                    <GridItemRow>
                        <Alert severity="error">
                            {`You are about to send SMS messages to customers outside of regular business hours (8 AM - 5 PM)`}
                        </Alert>
                    </GridItemRow>
                )}
                {afterHours && confirmationType === 'robocall' && (
                    <GridItemRow>
                        <Alert severity="error">
                            {`You are about to robocall customers outside of regular business hours (8 AM - 5 PM)`}
                        </Alert>
                    </GridItemRow>
                )}
                <GridItemRow>
                    <Alert severity="warning">
                        Verify all service times are correct. Customers only receive reschedule texts if timeframes
                        change by more than 30 minutes.
                    </Alert>
                </GridItemRow>
                <GridItemRow
                    css={css`
                        margin-top: 1.5rem !important;
                    `}
                >
                    <Body2>Customer communication options:</Body2>
                </GridItemRow>
                <RadioGroup
                    value={confirmationType}
                    onChange={(e) => setConfirmationType(e.target.value)}
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    `}
                >
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'shipperPreference'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Use each shipper's preferred confirmation method</Body1>
                            <Body2>
                                Each stop is confirmed according to its shipper's account setting. Orders whose shipper
                                hasn't set a preference will default to text & email.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'sms'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Text and email customers to confirm timeframes</Body1>
                            <Body2>
                                Onward will use automated messages to relay timeframes and receive confirmations.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'robocall'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Robocall customers to confirm timeframes</Body1>
                            <Body2>
                                Onward will place an automated phone call to relay timeframes and capture confirmations
                                via keypad response.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            value={'none'}
                            disabled={hasAutoconfirmAllStops}
                        />
                        <Grid container>
                            <Body1>Do not send automated communications</Body1>
                            <Body2>
                                No texts or emails will be sent. You'll need to contact customers and confirm timeframes
                                manually.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel control={<Radio color="primary" />} value={'autoConfirm'} />
                        <Grid container>
                            <Body1>Autoconfirm all timeframes</Body1>
                            <Body2>No texts or emails will be sent. All stops will be automatically confirmed.</Body2>
                        </Grid>
                    </GridItemRow>
                </RadioGroup>
                {missingPhoneNumbers.length > 0 && (
                    <Body2
                        style={{ color: 'red' }}
                        css={css`
                            margin-top: 1rem;
                        `}
                    >
                        Warning: The following orders are missing a customer phone number:{' '}
                        {missingPhoneNumbers.join(', ')}
                    </Body2>
                )}
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading} onClick={handleSubmitInternal}>
                    Submit Routes
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default RouteSubmitInternalModal;
